import React, { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";

// Chrome's own picker is where recordings are won or lost: the wrong surface or an unticked audio
// switch produces a silent file, and nobody finds out until the hearing is over. A written warning
// beside the button does not survive a user in a hurry, so both clicks are shown — the surface as a
// drawing of the tab strip, the audio switch as a recording of the real thing.
//
// A Mac below macOS 14.2 or Chrome 141 gets a different guide entirely: it has no system-audio
// switch to show, and pointing it at "Entire screen" would produce silence.

const Marker = ({ n }) => (
  <span className="absolute -right-2 -top-2 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground shadow">
    {n}
  </span>
);

const Step = ({ n, title, children }) => (
  <div className="flex gap-3">
    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
      {n}
    </span>
    <div className="min-w-0 flex-1">
      <p className="text-sm font-semibold text-foreground">{title}</p>
      <p className="mt-0.5 text-xs leading-5 text-muted-foreground">{children}</p>
    </div>
  </div>
);

// The row of surfaces Chrome offers, with the one that matters ringed.
const SurfaceStrip = ({ t, highlight }) => {
  const tabHighlighted = highlight === "tab";
  const on = "relative rounded px-2 py-1 font-semibold text-primary ring-2 ring-primary";
  const off = "rounded px-2 py-1 text-muted-foreground";

  return (
    <div className="rounded-lg border border-border bg-background p-2.5">
      <div className="flex flex-wrap items-center gap-1 text-[11px]">
        <span className={tabHighlighted ? on : off}>{t("Pestaña de Chrome", "Chrome Tab")}</span>
        <span className={off}>{t("Ventana", "Window")}</span>
        <span className={tabHighlighted ? off : on}>{t("Toda la pantalla", "Entire Screen")}</span>
      </div>
    </div>
  );
};

export default function ShareAudioGuide({ open, onOpenChange, onContinue, support }) {
  const { language } = useLanguage();
  const t = (es, en) => (language === "es" ? es : en);

  const [reducedMotion, setReducedMotion] = useState(false);
  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(query.matches);
    const onChange = (event) => setReducedMotion(event.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  // A Mac below macOS 14.2 or Chrome 141 can only ever share one tab's audio.
  const tabOnly = Boolean(support) && !support.systemAudio;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto" data-testid="share-audio-guide">
        <DialogHeader>
          <DialogTitle data-testid="share-audio-guide-title">
            {tabOnly
              ? t("En este computador hay que compartir la pestaña", "On this computer you must share the tab")
              : t("Dos pasos antes de grabar", "Two steps before recording")}
          </DialogTitle>
          <DialogDescription>
            {tabOnly
              ? t("Tu sistema no permite capturar el audio de todo el computador, así que el audio tiene que venir de una pestaña del navegador.",
                  "Your system cannot capture the whole computer's audio, so the audio must come from a browser tab.")
              : t("Chrome va a preguntarte qué compartir. Esto es lo que tienes que hacer:",
                  "Chrome will ask what to share. Here is what you need to do:")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-2">
            <Step
              n="1"
              title={tabOnly ? t('Elige "Pestaña de Chrome"', 'Pick "Chrome Tab"')
                             : t('Elige "Toda la pantalla"', 'Pick "Entire Screen"')}
            >
              {tabOnly
                ? t("Y selecciona la pestaña donde está el audio (Zoom, Meet o Teams desde el navegador).",
                    "Then select the tab the audio comes from (Zoom, Meet or Teams in the browser).")
                : t("Es la única opción que captura el sonido de Zoom, Teams o Meet instalados en el computador. Si compartes una ventana, no dejaremos empezar la grabación.",
                    "It is the only option that captures sound from Zoom, Teams or Meet installed on the computer. If you share a window, we will not let the recording start.")}
            </Step>
            <div className="pl-9">
              <SurfaceStrip t={t} highlight={tabOnly ? "tab" : "monitor"} />
            </div>
          </div>

          <div className="space-y-2">
            <Step
              n="2"
              title={tabOnly ? t("Deja activado el audio de la pestaña", "Leave the tab audio on")
                             : t("Activa el audio del sistema", "Turn on system audio")}
            >
              {tabOnly
                ? t("Suele venir activado. Si lo apagas, la grabación queda muda.",
                    "It is usually on already. If you turn it off, the recording is silent.")
                : t("Está abajo a la izquierda del cuadro de Chrome. Sin eso la grabación queda muda.",
                    "It sits at the bottom left of Chrome's dialog. Without it the recording is silent.")}
            </Step>

            {!tabOnly && (
              <div className="pl-9">
                <video
                  src="/share-system-audio.mp4"
                  poster="/share-system-audio.jpg"
                  autoPlay={!reducedMotion}
                  controls={reducedMotion}
                  loop
                  muted
                  playsInline
                  className="w-full rounded-lg border border-border bg-background"
                  data-testid="share-audio-guide-video"
                />
                <p className="mt-1.5 text-[11px] leading-4 text-muted-foreground">
                  {t("El texto exacto cambia según el idioma de tu Chrome.",
                     "The exact wording depends on your Chrome's language.")}
                </p>
              </div>
            )}
          </div>

          {tabOnly && (
            <div className="flex items-start gap-3 rounded-xl border-2 border-yellow-500/60 bg-yellow-500/15 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-yellow-400" />
              <p className="text-xs leading-5 text-yellow-200">
                {support?.os === "mac"
                  ? t("Si el audio viene de una aplicación de escritorio como Zoom o Teams, no habrá nada que compartir. Para capturarla necesitas macOS 14.2 o superior con Chrome 141 o superior, o instalar un cable de audio virtual como BlackHole.",
                      "If the audio comes from a desktop app such as Zoom or Teams there will be nothing to share. To capture it you need macOS 14.2 or later with Chrome 141 or later, or a virtual audio cable such as BlackHole.")
                  : t("Si el audio viene de una aplicación de escritorio, este sistema no puede capturar su sonido. Abre Voxara en Chrome sobre Windows, o usa un cable de audio virtual.",
                      "If the audio comes from a desktop app, this system cannot capture its sound. Open Voxara in Chrome on Windows, or use a virtual audio cable.")}
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:justify-between sm:space-x-0">
          <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}
                  data-testid="share-audio-guide-cancel">
            {t("Cancelar", "Cancel")}
          </Button>
          <Button type="button" className="btn-glow" onClick={onContinue}
                  data-testid="share-audio-guide-continue">
            {t("Entendido, continuar", "Got it, continue")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
